import { FocusEventHandler, useRef } from "react";
export interface UseFocusWithinOptions {
  onFocus?: (event: React.FocusEvent) => void;
  onBlur?: (event: React.FocusEvent) => void;
  otherRelatedElements?: HTMLElement[];
}

export interface UseFocusWithinReturnValue<
  T extends HTMLElement = HTMLElement,
> {
  props: {
    onFocus: FocusEventHandler<T>;
    onBlur: FocusEventHandler<T>;
  };
}

/**
 * Custom hook for tracking focus state within a DOM element.
 *
 * @param options Configuration options for the hook
 * @param options.onFocus Optional callback when focus enters the element
 * @param options.onBlur Optional callback when focus leaves the element
 * @returns Object containing props for the element
 *
 * @example
 * const { props } = useFocusWithin({
 *   onFocus: () => console.log('Focus entered'),
 *   onBlur: () => console.log('Focus left')
 * });
 *
 * return <div {...props}>Content</div>;
 */
export function useFocusWithin<T extends HTMLElement>({
  onBlur,
  onFocus,
  otherRelatedElements,
}: UseFocusWithinOptions = {}): UseFocusWithinReturnValue<T> {
  const isFocusedRef = useRef(false);

  const handleFocus: FocusEventHandler<T> = (event) => {
    if (!isFocusedRef.current) {
      isFocusedRef.current = true;
      onFocus?.(event);
    }
  };

  const handleBlur: FocusEventHandler<T> = (event) => {
    if (
      isFocusedRef.current &&
      !containsRelatedTarget(event) &&
      !otherRelatedElements?.some((element) =>
        element.contains(event.relatedTarget),
      )
    ) {
      isFocusedRef.current = false;
      onBlur?.(event);
    }
  };

  return {
    props: { onFocus: handleFocus, onBlur: handleBlur },
  };
}

function containsRelatedTarget(event: React.FocusEvent) {
  if (
    event.currentTarget instanceof HTMLElement &&
    event.relatedTarget instanceof HTMLElement
  ) {
    return event.currentTarget.contains(event.relatedTarget);
  }

  return false;
}
